import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MailX, Radar, RotateCcw } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fmtDateTimeMtl } from "../../lib/dateMtl";

// Onglet « Rebonds » (règle permanente du propriétaire, 2026-10-08) : journal des
// adresses retirées automatiquement des listes d'envoi après un retour
// « mail delivery system » sur la boîte no-reply@trophee.ca, avec réactivation.
export default function RebondsTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");   // id en cours de réactivation
  const [pollBusy, setPollBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/noreply/rebonds");
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const relever = async () => {
    setPollBusy(true);
    try {
      const { data } = await api.post("/admin/noreply/relever");
      if (data.skipped) toast.error("Boîte no-reply non configurée");
      else if (data.error) toast.error(`Relevé échoué : ${data.error}`);
      else toast.success(`Relevé terminé — ${data.lus || 0} message(s), ${data.rebonds || 0} adresse(s) retirée(s), ${data.reachemines || 0} réponse(s) acheminée(s)`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPollBusy(false);
    }
  };

  const reactiver = async (item) => {
    setBusy(item.id);
    try {
      const { data } = await api.post(`/admin/noreply/rebonds/${item.id}/reactiver`);
      if (data.deja) toast.info("Déjà réactivée");
      else toast.success(`${data.email} réactivée (${data.prospects} prospect(s), ${data.abonnes} abonné(s) infolettre)`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const items = data?.items || [];
  const actifs = items.filter((i) => !i.reactive_at);

  return (
    <div className="space-y-6" data-testid="rebonds-tab">
      <div className="flex flex-wrap items-center gap-4 border border-ivory/15 rounded-xl px-5 py-4">
        <MailX size={16} className="text-gold shrink-0" />
        <div className="flex-1 min-w-[260px]">
          <p className="text-sm font-semibold text-ivory">Rapport des rebonds — no-reply@trophee.ca</p>
          <p className="text-xs text-ivory/60 mt-1">
            Chaque retour « mail delivery system » retire l'adresse de la prospection et de l'infolettre,
            automatiquement, toutes les 5 minutes. Les réponses des clients sont acheminées à info@trophee.ca.
          </p>
        </div>
        <span className="text-xs text-ivory/60" data-testid="rebonds-count">
          <strong className={actifs.length ? "text-red-300" : "text-emerald-400"}>{actifs.length}</strong> retrait(s) actif(s)
          {items.length > actifs.length && ` · ${items.length - actifs.length} réactivé(s)`}
        </span>
        <button data-testid="rebonds-poll-btn" onClick={relever} disabled={pollBusy}
          className="inline-flex items-center gap-2 border border-ivory/25 text-ivory/70 px-4 py-1.5 rounded-full text-xs hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
          {pollBusy ? <Loader2 size={13} className="animate-spin" /> : <Radar size={13} />}
          Relever maintenant
        </button>
      </div>

      {!data && <p className="text-sm text-ivory/50 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</p>}

      {data && items.length === 0 && (
        <p className="text-sm text-emerald-400/90 border border-emerald-400/25 rounded-xl px-5 py-8 text-center" data-testid="rebonds-empty">
          Aucun rebond pour le moment — toutes les adresses des listes d'envoi sont valides.
        </p>
      )}

      {items.length > 0 && (
        <div className="border border-ivory/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="rebonds-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-ivory/45 border-b border-ivory/10">
                <th className="px-4 py-3">Date (Montréal)</th>
                <th className="px-4 py-3">Adresse retirée</th>
                <th className="px-4 py-3">Raison</th>
                <th className="px-4 py-3">Listes touchées</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-ivory/5" data-testid={`rebond-row-${it.id}`}>
                  <td className="px-4 py-3 text-ivory/70 whitespace-nowrap">{fmtDateTimeMtl(it.at)}</td>
                  <td className="px-4 py-3 text-ivory font-mono text-xs">{it.email}</td>
                  <td className="px-4 py-3 text-ivory/60 text-xs">{it.raison || it.subject || "—"}</td>
                  <td className="px-4 py-3 text-ivory/60 text-xs">
                    {it.prospects_retires ? `${it.prospects_retires} prospect(s)` : ""}
                    {it.prospects_retires && it.abonnes_retires ? " · " : ""}
                    {it.abonnes_retires ? `${it.abonnes_retires} abonné(s)` : ""}
                    {!it.prospects_retires && !it.abonnes_retires ? "aucune (déjà absente)" : ""}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {it.reactive_at ? (
                      <span className="text-xs text-emerald-400" data-testid={`rebond-reactive-${it.id}`}>
                        Réactivée le {fmtDateTimeMtl(it.reactive_at)}
                      </span>
                    ) : (
                      <button data-testid={`rebond-reactiver-${it.id}`} onClick={() => reactiver(it)}
                        disabled={busy === it.id}
                        className="inline-flex items-center gap-1.5 border border-gold/40 text-gold px-3 py-1 rounded-full text-xs hover:bg-gold/10 transition-colors disabled:opacity-50">
                        {busy === it.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                        Réactiver
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
